import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Siren, Gauge, CalendarClock, Inbox, LogOut, RefreshCw, Trash2, Search, ChevronDown, FileSearch, Plus, ClipboardList, Loader2, Droplets, CreditCard, Paperclip, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { clearSession, formatApiError } from "@/lib/api";
import { RecordFiles } from "@/components/portal/RecordFiles";
import { FleetAdmin } from "@/components/admin/FleetAdmin";
import { SERVICE_TYPES, BREAKDOWN_TYPE, BMW_TYPE } from "@/lib/site";

const RECORD_EMPTY = { rego: "", customer_name: "", customer_phone: "", customer_email: "", vehicle: "", service_date: "", odometer_km: "", title: "", description: "", cost: "", next_service_due: "", oil_change: false, oil_grade: "", oil_interval_km: "", oil_interval_months: "" };

const STATUS_TABS = ["all", "new", "contacted", "completed"];
const NEXT_STATUS = { new: "contacted", contacted: "completed" };

const inputCls =
  "border border-white/10 bg-[#12151C] px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-200 focus:border-amber-600/60";

const ServiceBadge = ({ item }) => {
  if (item.is_breakdown)
    return (
      <span className="inline-flex items-center gap-1.5 border border-red-500/50 bg-red-500/15 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-red-400" data-testid={`badge-breakdown-${item.id}`}>
        <Siren size={11} /> Urgent Breakdown
      </span>
    );
  if (item.is_bmw_diagnostic)
    return (
      <span className="inline-flex items-center gap-1.5 border border-blue-500/50 bg-blue-500/15 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-blue-400" data-testid={`badge-bmw-${item.id}`}>
        <Gauge size={11} /> BMW Diagnostic
      </span>
    );
  if (item.is_history_check)
    return (
      <span className="inline-flex items-center gap-1.5 border border-teal-500/50 bg-teal-500/15 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-teal-400" data-testid={`badge-history-${item.id}`}>
        <FileSearch size={11} /> History Check
      </span>
    );
  return (
    <span className="inline-flex items-center border border-white/15 bg-white/5 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
      {item.service_type}
    </span>
  );
};

const StatusPill = ({ status }) => {
  const styles = {
    new: "border-amber-600/50 bg-amber-600/10 text-amber-500",
    contacted: "border-blue-500/40 bg-blue-500/10 text-blue-400",
    completed: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  };
  return (
    <span className={`inline-block border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${styles[status] || styles.new}`} data-testid={`status-pill-${status}`}>
      {status}
    </span>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusTab, setStatusTab] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [view, setView] = useState("enquiries");
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [showRecordForm, setShowRecordForm] = useState(false);
  const [recordForm, setRecordForm] = useState(RECORD_EMPTY);
  const [recordSaving, setRecordSaving] = useState(false);
  const [aiModels, setAiModels] = useState({ list: [], current: "" });
  const [openFiles, setOpenFiles] = useState(null);

  const loadRecords = useCallback(async () => {
    setRecordsLoading(true);
    try {
      const { data } = await api.get("/admin/service-records");
      setRecords(data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
      else toast.error("Failed to load service records");
    } finally {
      setRecordsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (view === "records") loadRecords();
  }, [view, loadRecords]);

  const setRec = (k) => (e) => setRecordForm((f) => ({ ...f, [k]: e.target.value }));

  const submitRecord = async (e) => {
    e.preventDefault();
    if (!recordForm.customer_phone.trim() && !recordForm.customer_email.trim()) {
      toast.error("Enter at least one contact: a phone number or an email address");
      return;
    }
    setRecordSaving(true);
    try {
      const payload = {};
      Object.entries(recordForm).forEach(([k, v]) => { if (typeof v === "boolean" ? v : String(v).trim()) payload[k] = v; });
      ["odometer_km", "cost", "oil_interval_km", "oil_interval_months"].forEach((k) => { if (payload[k]) payload[k] = Number(payload[k]); });
      if (!payload.oil_change) ["oil_grade", "oil_interval_km", "oil_interval_months"].forEach((k) => delete payload[k]);
      await api.post("/admin/service-records", payload);
      toast.success("Service record added");
      setRecordForm(RECORD_EMPTY);
      setShowRecordForm(false);
      loadRecords();
    } catch (err) {
      toast.error(formatApiError(err, "Could not save record"));
    } finally {
      setRecordSaving(false);
    }
  };

  const deleteRecord = async (r) => {
    try {
      await api.delete(`/admin/service-records/${r.id}`);
      toast.success("Record deleted");
      loadRecords();
    } catch {
      toast.error("Delete failed");
    }
  };

  const load = useCallback(async () => {
    try {
      const params = {};
      if (statusTab !== "all") params.status = statusTab;
      if (serviceFilter) params.service_type = serviceFilter;
      if (search.trim()) params.q = search.trim();
      const [s, e] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/enquiries", { params }),
      ]);
      setStats(s.data);
      setItems(e.data);
    } catch (err) {
      if (err.response?.status === 401) {
        clearSession();
        navigate("/admin/login");
      } else {
        toast.error("Failed to load enquiries");
      }
    } finally {
      setLoading(false);
    }
  }, [statusTab, serviceFilter, search, navigate]);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => (data.role === "admin" ? setUser(data) : navigate("/admin/login")))
      .catch(() => navigate("/admin/login"));
    api.get("/ai/models").then(({ data }) => setAiModels({ list: data.models, current: data.default })).catch(() => {});
  }, [navigate]);

  const changeAiModel = async (key) => {
    try {
      await api.put("/ai/admin/default-model", { model: key });
      setAiModels((m) => ({ ...m, current: key }));
      toast.success("Default AI model updated");
    } catch {
      toast.error("Could not update AI model");
    }
  };

  const setRecordPayment = async (r, payment_status) => {
    try {
      await api.patch(`/admin/service-records/${r.id}/payment`, { payment_status });
      toast.success(`Marked ${payment_status.replace("_", " ")}`);
      loadRecords();
    } catch {
      toast.error("Update failed");
    }
  };

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const setStatus = async (item, status) => {
    try {
      await api.patch(`/admin/enquiries/${item.id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch {
      toast.error("Status update failed");
    }
  };

  const remove = async (item) => {
    try {
      await api.delete(`/admin/enquiries/${item.id}`);
      toast.success("Enquiry deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearSession();
    navigate("/admin/login");
  };

  const kpis = stats
    ? [
        { label: "Total Enquiries", value: stats.total, icon: Inbox, cls: "text-slate-100", testid: "kpi-total" },
        { label: "Urgent Breakdowns", value: stats.urgent_breakdowns, icon: Siren, cls: "text-red-400", testid: "kpi-breakdowns" },
        { label: "BMW Diagnostics", value: stats.bmw_diagnostics, icon: Gauge, cls: "text-blue-400", testid: "kpi-bmw" },
        { label: "This Week", value: stats.this_week, icon: CalendarClock, cls: "text-amber-500", testid: "kpi-week" },
      ]
    : [];

  return (
    <div className="App min-h-screen" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0A0C10]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
          <div className="flex items-center gap-3">
            <span className="font-display text-xl tracking-[0.15em] text-slate-50">SAGITTARIUS</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">Workshop Control</span>
          </div>
          <div className="flex items-center gap-4">
            {aiModels.list.length > 0 && (
              <label className="hidden items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 lg:flex">
                <Sparkles size={12} className="text-amber-500" /> AI
                <select value={aiModels.current} onChange={(e) => changeAiModel(e.target.value)} className="border border-white/10 bg-[#12151C] px-2 py-1.5 text-[10px] uppercase tracking-[0.15em] text-slate-300 outline-none" data-testid="admin-ai-model-select">
                  {aiModels.list.map((m) => <option key={m.key} value={m.key} className="bg-[#12151C]">{m.label}</option>)}
                </select>
              </label>
            )}
            {user && <span className="hidden font-mono text-xs text-slate-500 sm:block" data-testid="admin-user-email">{user.email}</span>}
            <button onClick={() => { setLoading(true); load(); }} data-testid="admin-refresh-btn" className="border border-white/15 p-2 text-slate-400 transition-colors hover:border-amber-600 hover:text-amber-500">
              <RefreshCw size={15} />
            </button>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 transition-colors hover:border-red-500/60 hover:text-red-400">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="kpi-row">
          {kpis.map((k, i) => (
            <motion.div
              key={k.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="border border-white/10 bg-[#12151C] p-5"
              data-testid={k.testid}
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{k.label}</span>
                <k.icon size={16} className={k.cls} />
              </div>
              <div className={`mt-3 font-display text-4xl ${k.cls}`} data-testid={`${k.testid}-value`}>{k.value}</div>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 flex w-max border border-white/10" data-testid="admin-view-tabs">
          <button
            onClick={() => setView("enquiries")}
            data-testid="admin-tab-enquiries"
            className={`px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${view === "enquiries" ? "bg-amber-600 text-black" : "text-slate-400 hover:text-slate-100"}`}
          >
            Enquiries
          </button>
          <button
            onClick={() => setView("records")}
            data-testid="admin-tab-records"
            className={`px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${view === "records" ? "bg-amber-600 text-black" : "text-slate-400 hover:text-slate-100"}`}
          >
            Service Records
          </button>
          <button
            onClick={() => setView("fleet")}
            data-testid="admin-tab-fleet"
            className={`px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${view === "fleet" ? "bg-amber-600 text-black" : "text-slate-400 hover:text-slate-100"}`}
          >
            Fleet Customers
          </button>
        </div>

        {view === "fleet" && <FleetAdmin />}

        {view === "enquiries" && (<>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="flex border border-white/10" data-testid="status-tabs">
            {STATUS_TABS.map((s) => (
              <button
                key={s}
                onClick={() => setStatusTab(s)}
                data-testid={`status-tab-${s}`}
                className={`px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                  statusTab === s ? "bg-amber-600 text-black" : "text-slate-400 hover:text-slate-100"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)} className={inputCls} data-testid="service-filter-select">
            <option value="" className="bg-[#12151C]">All services</option>
            {SERVICE_TYPES.map((s) => (
              <option key={s} value={s} className="bg-[#12151C]">{s}</option>
            ))}
          </select>
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, vehicle, phone…"
              className={`${inputCls} w-full pl-9`}
              data-testid="enquiry-search-input"
            />
          </div>
        </div>

        <div className="mt-6 space-y-3" data-testid="enquiry-list">
          {loading && <p className="py-16 text-center font-mono text-xs uppercase tracking-[0.25em] text-slate-600">Loading…</p>}
          {!loading && items.length === 0 && (
            <p className="border border-white/10 py-16 text-center font-mono text-xs uppercase tracking-[0.25em] text-slate-600" data-testid="enquiry-empty">
              No enquiries match
            </p>
          )}
          {!loading && items.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`border bg-[#12151C] ${item.is_breakdown && item.status !== "completed" ? "border-red-500/40" : "border-white/10"}`}
              data-testid={`enquiry-row-${item.id}`}
            >
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 px-5 py-4">
                <button onClick={() => setExpanded(expanded === item.id ? null : item.id)} className="flex items-center gap-2 text-left" data-testid={`enquiry-expand-${item.id}`}>
                  <ChevronDown size={14} className={`text-slate-500 transition-transform ${expanded === item.id ? "rotate-180" : ""}`} />
                  <div>
                    <div className="text-sm font-semibold text-slate-100">{item.name}</div>
                    <div className="font-mono text-[11px] text-slate-500">{item.vehicle}</div>
                  </div>
                </button>
                <ServiceBadge item={item} />
                <StatusPill status={item.status} />
                <div className="ml-auto flex items-center gap-4">
                  <div className="text-right">
                    <div className="font-mono text-xs text-slate-300">{item.phone}</div>
                    <div className="font-mono text-[10px] text-slate-600">{new Date(item.created_at).toLocaleString("en-NZ", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                  {NEXT_STATUS[item.status] && (
                    <button
                      onClick={() => setStatus(item, NEXT_STATUS[item.status])}
                      data-testid={`enquiry-advance-${item.id}`}
                      className="border border-amber-600/50 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-amber-500 transition-colors hover:bg-amber-600 hover:text-black"
                    >
                      Mark {NEXT_STATUS[item.status]}
                    </button>
                  )}
                  <button onClick={() => remove(item)} data-testid={`enquiry-delete-${item.id}`} className="p-2 text-slate-600 transition-colors hover:text-red-400">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              {expanded === item.id && (
                <div className="border-t border-white/5 px-5 py-4" data-testid={`enquiry-detail-${item.id}`}>
                  <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-3">
                    <div><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">Email</span><p className="mt-1 text-slate-300">{item.email}</p></div>
                    <div><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">Preferred date</span><p className="mt-1 text-slate-300">{item.preferred_date || "—"}</p></div>
                    <div><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">Reference</span><p className="mt-1 font-mono text-amber-500">{item.id.slice(0, 8).toUpperCase()}</p></div>
                    {item.rego && <div><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">Rego plate</span><p className="mt-1 font-mono text-slate-200">{item.rego}</p></div>}
                    {item.vin && <div><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">VIN</span><p className="mt-1 font-mono text-slate-200">{item.vin}</p></div>}
                    {item.purpose && <div><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">Purpose</span><p className="mt-1 text-slate-300">{item.purpose}</p></div>}
                  </div>
                  {item.message && (
                    <div className="mt-4">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">Customer message</span>
                      <p className="mt-1 leading-relaxed text-slate-300">{item.message}</p>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          ))}
        </div>
        </>)}

        {view === "records" && (
          <div className="mt-6" data-testid="records-view">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">Customer vehicle service records</p>
              <button onClick={() => setShowRecordForm(!showRecordForm)} data-testid="record-add-toggle" className="flex items-center gap-2 border border-amber-600/60 bg-amber-600/10 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 transition-colors hover:bg-amber-600 hover:text-black">
                <Plus size={13} /> Add record
              </button>
            </div>

            {showRecordForm && (
              <form onSubmit={submitRecord} className="mt-4 border border-white/10 bg-[#12151C] p-6" data-testid="record-form">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <input required value={recordForm.rego} onChange={setRec("rego")} placeholder="REGO PLATE *" className={`${inputCls} font-mono uppercase`} data-testid="record-rego-input" />
                  <input required value={recordForm.customer_name} onChange={setRec("customer_name")} placeholder="Customer name *" className={inputCls} data-testid="record-name-input" />
                  <input value={recordForm.customer_phone} onChange={setRec("customer_phone")} placeholder="Customer phone (phone or email required)" className={inputCls} data-testid="record-phone-input" />
                  <input type="email" value={recordForm.customer_email} onChange={setRec("customer_email")} placeholder="Customer email (phone or email required)" className={inputCls} data-testid="record-email-input" />
                  <input value={recordForm.vehicle} onChange={setRec("vehicle")} placeholder="Vehicle (e.g. BMW 320i 2019)" className={inputCls} data-testid="record-vehicle-input" />
                  <input required type="date" value={recordForm.service_date} onChange={setRec("service_date")} className={inputCls} data-testid="record-date-input" />
                  <input required value={recordForm.title} onChange={setRec("title")} placeholder="Work done (e.g. Full service) *" className={inputCls} data-testid="record-title-input" />
                  <input type="number" min="0" value={recordForm.odometer_km} onChange={setRec("odometer_km")} placeholder="Odometer (km)" className={inputCls} data-testid="record-odometer-input" />
                  <input type="number" min="0" step="0.01" value={recordForm.cost} onChange={setRec("cost")} placeholder="Cost (NZD)" className={inputCls} data-testid="record-cost-input" />
                  <input type="date" value={recordForm.next_service_due} onChange={setRec("next_service_due")} className={inputCls} data-testid="record-nextdue-input" title="Next service due" />
                </div>
                <textarea rows={3} value={recordForm.description} onChange={setRec("description")} placeholder="Notes — parts used, work performed…" className={`${inputCls} mt-4 w-full resize-none`} data-testid="record-description-input" />

                <div className={`mt-4 border p-4 transition-colors ${recordForm.oil_change ? "border-amber-600/50 bg-amber-600/5" : "border-white/10"}`} data-testid="record-oiltrac-panel">
                  <label className="flex cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      checked={recordForm.oil_change}
                      onChange={(e) => setRecordForm((f) => ({ ...f, oil_change: e.target.checked }))}
                      className="h-4 w-4 accent-amber-600"
                      data-testid="record-oilchange-checkbox"
                    />
                    <Droplets size={15} className="text-amber-500" />
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-200">Oil Trac — oil changed on this visit</span>
                  </label>
                  {recordForm.oil_change && (
                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <input value={recordForm.oil_grade} onChange={setRec("oil_grade")} placeholder="Oil grade (e.g. 5W-30 LL-04)" className={inputCls} data-testid="record-oilgrade-input" />
                      <input type="number" min="500" step="500" value={recordForm.oil_interval_km} onChange={setRec("oil_interval_km")} placeholder="Interval km (default 10,000)" className={inputCls} data-testid="record-oilinterval-km-input" />
                      <input type="number" min="1" max="36" value={recordForm.oil_interval_months} onChange={setRec("oil_interval_months")} placeholder="Interval months (default 12)" className={inputCls} data-testid="record-oilinterval-months-input" />
                    </div>
                  )}
                </div>
                <button type="submit" disabled={recordSaving} data-testid="record-submit-button" className="mt-4 flex items-center gap-2 bg-amber-600 px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-amber-500 disabled:opacity-60">
                  {recordSaving ? <Loader2 size={14} className="animate-spin" /> : <ClipboardList size={14} />} Save record
                </button>
              </form>
            )}

            <div className="mt-4 space-y-3" data-testid="records-list">
              {recordsLoading && <p className="py-12 text-center font-mono text-xs uppercase tracking-[0.25em] text-slate-600">Loading…</p>}
              {!recordsLoading && records.length === 0 && (
                <p className="border border-white/10 py-12 text-center font-mono text-xs uppercase tracking-[0.25em] text-slate-600" data-testid="records-empty">No service records yet</p>
              )}
              {!recordsLoading && records.map((r) => (
                <div key={r.id} className="border border-white/10 bg-[#12151C]" data-testid={`record-row-${r.id}`}>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-5 py-4">
                  <span className="border border-teal-500/40 bg-teal-500/10 px-2.5 py-1 font-mono text-[11px] tracking-[0.15em] text-teal-400">{r.rego}</span>
                  <div>
                    <div className="text-sm font-semibold text-slate-100">{r.title}</div>
                    <div className="font-mono text-[11px] text-slate-500">{r.customer_name}{r.vehicle ? ` — ${r.vehicle}` : ""}</div>
                  </div>
                  {r.oil_change && (
                    <span className="inline-flex items-center gap-1.5 border border-amber-600/50 bg-amber-600/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-amber-500" data-testid={`record-oil-badge-${r.id}`}>
                      <Droplets size={11} /> Oil {r.oil_grade || "changed"}{r.next_oil_due_km != null ? ` · due ${r.next_oil_due_km.toLocaleString()} km` : ""}
                    </span>
                  )}
                  {r.payment_status === "paid" && (
                    <span className="inline-flex items-center gap-1.5 border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-400" data-testid={`record-payment-paid-${r.id}`}><CreditCard size={11} /> Paid</span>
                  )}
                  {r.payment_status === "unpaid" && (
                    <span className="inline-flex items-center gap-1.5 border border-red-500/40 bg-red-500/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-red-400" data-testid={`record-payment-unpaid-${r.id}`}><CreditCard size={11} /> Unpaid</span>
                  )}
                  <div className="ml-auto flex items-center gap-5">
                    <div className="text-right font-mono text-[11px] text-slate-500">
                      <div>{r.service_date}</div>
                      {r.odometer_km != null && <div>{r.odometer_km.toLocaleString()} km</div>}
                      {r.cost != null && <div className="text-amber-500">NZ${r.cost}</div>}
                    </div>
                    {r.payment_status === "unpaid" && (
                      <button onClick={() => setRecordPayment(r, "paid")} data-testid={`record-mark-paid-${r.id}`} className="border border-emerald-500/50 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-400 transition-colors hover:bg-emerald-500 hover:text-black">Mark paid</button>
                    )}
                    {r.payment_status === "paid" && (
                      <button onClick={() => setRecordPayment(r, "unpaid")} data-testid={`record-mark-unpaid-${r.id}`} className="border border-white/15 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400 transition-colors hover:text-red-400">Undo paid</button>
                    )}
                    <button onClick={() => setOpenFiles(openFiles === r.id ? null : r.id)} data-testid={`record-files-toggle-${r.id}`} className={`p-2 transition-colors hover:text-amber-500 ${openFiles === r.id ? "text-amber-500" : "text-slate-500"}`} title="Attachments"><Paperclip size={15} /></button>
                    <button onClick={() => deleteRecord(r)} data-testid={`record-delete-${r.id}`} className="p-2 text-slate-600 transition-colors hover:text-red-400"><Trash2 size={15} /></button>
                  </div>
                </div>
                {openFiles === r.id && <div className="border-t border-white/5 px-5 pb-5"><RecordFiles recordId={r.id} admin /></div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
